import {
  IPdfDefinitions,
  IPdfData,
  PdfContentType,
  IPdfValueParams,
  PdfFiledValue,
  SEARCH_SUMMARY_DEFINE,
  PdfContentSetting,
} from '@app/shared/common/make-pdf/v2';
import {
  IInventoryItem,
  IMeasure,
  MEASURE_TRANSACTION,
} from '@app/shared/types';
import {
  generateSpacesHeader,
  generateSpacesText,
  groupByMajorMinorCategoryAndSort,
  getSingleExportFileName,
  formatDateWithTimeZone,
  isCategoryView,
  generateUnitTextV2,
} from '@app/shared/utils';
import { ContentText, Style } from 'pdfmake/interfaces';
import {
  ORDER_STATUS_ENUM_OPTIONS,
  ORDER_STATUS_VALUE,
} from '@app/shared/types/enums/order-status.enum';
import { includes, get, find, isEqual, isEmpty, isNil, size } from 'lodash';
import { decimalNumber } from '@app/shared/common';
import { TIME_FORMATS } from '@config/global-constants';
import {
  ORDER_PDF_FOR_OPEN_OR_CANCEL,
  ORDER_PDF_HEADERS_CONFIG,
} from '@app/shared/types/enums/order-pdf.enum';
import { ORDER_FORM_FIELD_PATHS } from './order-form-field-paths';
import { PdfWorkerSelfType } from '@app/shared/common/make-pdf/pdf-worker.type';
import { I18n } from '@app/i18n/strings-keys';

declare const self: PdfWorkerSelfType;

const buildOrderPdfConfig = (
  data: IPdfDefinitions['data'],
  isShowAllColumns: boolean,
  bulkOptions?: IPdfDefinitions['bulkOptions'],
  useDefaultSort: boolean = false,
): IPdfDefinitions => ({
  bulkOptions,
  data,
  // not used when just export by browser
  generateFileName: (pdfData): string => {
    const headerPrefix = includes(
      [ORDER_STATUS_VALUE.Canceled, ORDER_STATUS_VALUE.Open],
      pdfData?.status,
    )
      ? 'Order Docket'
      : 'Delivery Docket';
    const vendorName: string = get(
      pdfData,
      ORDER_FORM_FIELD_PATHS.vendorName,
    ) as string;
    const transactionDate = get(pdfData, 'transaction_date') as string;

    return getSingleExportFileName(headerPrefix, vendorName, transactionDate);
  },
  generateHeader: (pdfData: IPdfData, tools): ContentText[] => {
    const headerPrefix = includes(
      [ORDER_STATUS_VALUE.Canceled, ORDER_STATUS_VALUE.Open],
      pdfData?.status,
    )
      ? I18n.Forms.GridForm.OrderDocket
      : I18n.Forms.GridForm.DeliveryDocket;

    return [
      {
        text: generateSpacesHeader(
          tools.services?.i18nService
            ? tools.services.i18nService.instant(headerPrefix)
            : headerPrefix,
          pdfData?.internal_number?.toString(),
        ),
        fontSize: 14,
        bold: true,
        margin: [20, 20, 20, 0],
      },
    ];
  },
  content: {
    summary: {
      type: PdfContentType.Summary,
      setting: {
        fields: [
          {
            [I18n.EntityNames.Vendor]: {
              path: 'vendor_name',
              width: '30%',
            },
            [I18n.Forms.OrderColumns.OrderDate]: {
              path: 'transaction_date',
              valueGetter: (params): string =>
                formatDateWithTimeZone(
                  params.value?.toString(),
                  params.tools.services.companyService.currentCompany?.timezone,
                  TIME_FORMATS.L,
                ),
              width: '20%',
            },
            [I18n.Forms.OrderColumns.ExternalRefNo]: {
              path: 'external_order_number',
              width: '35%',
            },
            [I18n.Common.Status]: {
              path: 'status',
              valueFormatter: (params: IPdfValueParams): string =>
                params.tools.services.i18nService.instant(
                  ORDER_STATUS_ENUM_OPTIONS[params.value?.toString()],
                ),
              width: '15%',
            },
          },
          {
            [I18n.EntityNames.Warehouse]: {
              valueGetter: (params: IPdfValueParams): string =>
                generateSpacesText(
                  params.data?.site_name?.toString(),
                  params?.data.store_number?.toString(),
                ),
              width: '30%',
            },
            [I18n.Common.DeliveryDate]: {
              path: 'delivery_date',
              valueGetter: (params): string =>
                formatDateWithTimeZone(
                  params.value?.toString(),
                  params.tools.services.companyService.currentCompany?.timezone,
                  TIME_FORMATS.L,
                ),
              width: '20%',
            },
            [I18n.Forms.OrderColumns.InvoiceNo]: {
              path: 'invoice_number',
              width: '35%',
            },
          },
          SEARCH_SUMMARY_DEFINE,
        ],
      },
    },
    mainGrids: {
      type: PdfContentType.Grids,
      setting: (pdfData: IPdfData): PdfContentSetting => {
        const isOpenStatus = [
          ORDER_STATUS_VALUE.New,
          ORDER_STATUS_VALUE.Open,
        ].includes(pdfData?.status);
        const isOpenOrCanceled = pdfData.isOpenOrCanceledStatus;

        const hiddenReceivedColumn = (): boolean => isOpenOrCanceled;

        return {
          [ORDER_FORM_FIELD_PATHS.virtualInventoryItemVenues]: {
            title: ({ gridData, tools }): string => {
              return isCategoryView(
                tools?.services?.stateService?.getOrderItemListViewSetting(),
              )
                ? tools.services.i18nService.instant(
                    I18n.Forms.GridForm.ExportMajorMinorTitle,
                    {
                      majorCategory:
                        gridData[0]?.major_category_name ??
                        gridData[0]?.majorCategory ??
                        '',
                      minorCategory:
                        gridData[0]?.minor_category_name ??
                        gridData[0]?.minorCategory ??
                        '',
                    },
                  )
                : null;
            },
            orderBy: (): string[] | null =>
              !!useDefaultSort ? ['name asc', 'item_name asc'] : null,
            columns: [
              {
                path: 'name',
                title: I18n.Forms.GridForm.ItemName,
                valueGetter: (params): string =>
                  params.value ?? params.rowData?.item_name,
                width: isOpenOrCanceled ? '30%' : null,
              },
              {
                path: 'gl_account_name',
                title: I18n.Forms.GridForm.GLAccount,
                width: isOpenOrCanceled ? '15%' : null,
              },
              {
                path: 'measures',
                title: I18n.Forms.GridForm.OrderingUnit,
                width: isOpenOrCanceled ? 'auto' : null,
                valueFormatter: ({ tools, ...params }): string => {
                  let targetMeasure: Partial<IMeasure>;

                  if (!isEmpty(params.value)) {
                    targetMeasure = find(params.value, (measure) =>
                      isEqual(
                        measure.entity_id,
                        params.rowData?.measure_entity_id,
                      ),
                    );
                  } else {
                    const isDefaultMeasure = find(params.rowData.measures, {
                      entity_id: get(params.rowData, 'measure_entity_id'),
                    });

                    targetMeasure = {
                      id: get(params.rowData, 'measure_id'),
                      entity_id: get(params.rowData, 'measure_entity_id'),
                      description: get(params.rowData, 'measure_description'),
                      factor_value: get(params.rowData, 'measure_factor_value'),
                      code: get(params.rowData, 'measure_code'),
                      is_default: get(isDefaultMeasure, 'is_default', false),
                    };
                  }

                  return generateUnitTextV2(targetMeasure, {
                    numberFormatService: tools.services.numberFormatService,
                    measureTransactionType: MEASURE_TRANSACTION.Order,
                    notNeedAddSign: true,
                  });
                },
              },
              {
                path: 'initial_count',
                title: I18n.Forms.GridForm.QuantityOrdered,
                width: isOpenOrCanceled ? '*' : null,
                alignment: 'right',
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        false,
                      )(params),
              },
              {
                path: isOpenStatus ? 'item_cost' : 'initial_item_cost',
                title: I18n.Forms.GridForm.UnitCost,
                width: isOpenOrCanceled ? '*' : null,
                alignment: 'right',
                valueFormatter: ({ tools, ...params }): string =>
                  tools.services.numberFormatService.getNumberFormatter(true)(
                    params,
                  ),
              },
              {
                path: isOpenOrCanceled ? 'total_cost' : 'initial_total_cost',
                title: I18n.Forms.GridForm.OrderExtendedCost,
                width: isOpenOrCanceled ? '*' : null,
                alignment: 'right',
                valueGetter: (params): string => {
                  if (
                    includes(
                      [ORDER_STATUS_VALUE.New, ORDER_STATUS_VALUE.Open],
                      get(params, 'data.status'),
                    )
                  ) {
                    return decimalNumber(params?.rowData?.initial_count)
                      .mul(params?.rowData?.initial_item_cost)
                      ?.toString();
                  }

                  return decimalNumber(
                    params?.rowData?.initial_total_cost,
                  )?.toString();
                },
                valueFormatter: ({ tools, ...params }): string =>
                  tools.services.numberFormatService.getNumberFormatter(true)(
                    params,
                  ),
              },
              {
                hiddenColumn: hiddenReceivedColumn,
                path: 'count',
                title: I18n.Forms.GridForm.QuantityReceived,
                alignment: 'right',
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        false,
                      )(params),
              },
              {
                hiddenColumn: hiddenReceivedColumn,
                path: 'item_cost',
                title: I18n.Forms.GridForm.ReceivedUnitCost,
                alignment: 'right',
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        true,
                      )(params),
              },
              {
                hiddenColumn: hiddenReceivedColumn,
                path: 'total_cost',
                title: I18n.Forms.GridForm.ReceivedExtendedCost,
                alignment: 'right',
                valueGetter: (params): string => {
                  if (
                    includes(
                      [ORDER_STATUS_VALUE.Pending],
                      get(params, 'data.status'),
                    )
                  ) {
                    return decimalNumber(params?.rowData?.count)
                      .mul(params?.rowData?.item_cost)
                      ?.toString();
                  }

                  return decimalNumber(params?.rowData?.total_cost)?.toString();
                },
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        true,
                      )(params),
              },
            ],
            multiGrid: (
              params,
            ):
              | Record<string, IInventoryItem[]>
              | PdfFiledValue<IInventoryItem>[] =>
              isCategoryView(
                params.tools?.services?.stateService?.getOrderItemListViewSetting(),
              )
                ? groupByMajorMinorCategoryAndSort(params?.data)
                : [params.data],
          },
          [ORDER_FORM_FIELD_PATHS.virtualTotalStatus]: {
            title: {
              text: I18n.Common.Totals,
              style: {
                fontSize: 11,
                bold: true,
                margin: [0, 10, 0, 5],
              },
            },
            rowStyle: (params): Style => {
              const isLastRow = isEqual(
                size(params.gridData) - 1,
                params.rowIndex,
              );

              return {
                bold: isLastRow,
              };
            },
            layout: {
              fillColor: (rowIndex): string => {
                return rowIndex % 2 === 0
                  ? self.greyBgColor
                  : self.whiteBgColor;
              },
            },
            columnDefaultStyle: {
              width: '*',
              alignment: 'right',
            },
            orderBy: ['name asc'],
            columns: [
              {
                path: 'name',
                title: I18n.Forms.GridForm.GLAccount,
                width: '*',
                alignment: null,
                required: true,
              },
              {
                path: 'count',
                title: I18n.Forms.GridForm.QuantityOrdered,
                valueFormatter: ({ tools, ...params }): string =>
                  tools.services.numberFormatService.getNumberFormatter(false)(
                    params,
                  ),
              },
              {
                path: 'total_cost',
                title: I18n.Forms.GridForm.OrderExtendedCost,
                valueFormatter: ({ tools, ...params }): string =>
                  tools.services.numberFormatService.getNumberFormatter(true)(
                    params,
                  ),
              },
              {
                path: 'quantity_received',
                title: I18n.Forms.GridForm.QuantityReceived,
                hiddenColumn: hiddenReceivedColumn,
                valueFormatter: ({ tools, ...params }): string =>
                  tools.services.numberFormatService.getNumberFormatter(false)(
                    params,
                  ),
              },
              {
                path: 'received_total_cost',
                title: I18n.Forms.GridForm.ReceivedExtendedCost,
                hiddenColumn: hiddenReceivedColumn,
                valueFormatter: ({ tools, ...params }): string =>
                  tools.services.numberFormatService.getNumberFormatter(true)(
                    params,
                  ),
              },
            ],
          },
        };
      },
    },
    grandTotal: {
      type: PdfContentType.Grids,
      hidden: (pdfData: IPdfData, pdfDefinitions): boolean => {
        const hasSelectColumn = find(
          pdfDefinitions.selectedColumns,
          (column) => {
            // there is only `title` property, no `path` property, don't replace it easily.

            return isEqual(
              I18n.Forms.GridForm.ReceivedExtendedCost,
              column.title,
            );
          },
        );

        return pdfData.isOpenOrCanceledStatus || isEmpty(hasSelectColumn);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setting: (pdfData: IPdfData): PdfContentSetting => {
        return {
          'virtual.grand_totals': {
            alwaysInOnePage: true,
            title: {
              text: I18n.Forms.OrderForm.OrderSummary,
              style: {
                fontSize: 11,
                bold: true,
                margin: [0, 10, 0, 5],
              },
            },
            rowStyle: {
              bold: true,
            },
            layout: {
              fillColor: (rowIndex): string => {
                return rowIndex % 2 === 0
                  ? self.greyBgColor
                  : self.whiteBgColor;
              },
            },
            columnDefaultStyle: {
              width: '*',
              alignment: 'right',
              valueFormatter: ({ tools, ...params }): string =>
                isNaN(Number(params.value?.toString()))
                  ? params.value
                  : tools.services.numberFormatService.getNumberFormatter(true)(
                      params,
                    ),
            },
            columns: [
              {
                path: 'total_cost_show',
                title: I18n.Forms.GridForm.TotalItemsCost,
              },
              {
                path: 'delivery_fee',
                title: I18n.Forms.GridForm.DeliveryFee,
              },
              {
                path: 'tax_fee',
                title: I18n.Forms.GridForm.SalesTax,
              },
              {
                path: 'grand_total',
                title: I18n.Common.GrandTotal,
              },
            ],
          },
        };
      },
    },
    signatures: {
      type: PdfContentType.Signatures,
      setting: (pdfData: IPdfData): string[] => {
        if (
          includes(
            [ORDER_STATUS_VALUE.Open, ORDER_STATUS_VALUE.Canceled],
            pdfData?.status,
          )
        ) {
          return [
            I18n.Forms.GridForm.OrderedBy,
            I18n.Forms.GridForm.ApprovedBy,
          ];
        }

        if (
          includes(
            [
              ORDER_STATUS_VALUE.Pending,
              ORDER_STATUS_VALUE.Completed,
              ORDER_STATUS_VALUE.Rejected,
            ],
            pdfData?.status,
          )
        ) {
          return [
            I18n.Forms.GridForm.DeliveredBy,
            I18n.Forms.GridForm.ReceivedBy,
          ];
        }
      },
    },
  },
  options: {
    pageMargins: [20, 50, 20, 60],
    columnSelector: {
      columns: isShowAllColumns
        ? ORDER_PDF_HEADERS_CONFIG
        : ORDER_PDF_FOR_OPEN_OR_CANCEL,
      effectCondition: {
        type: 'excludes',
        paths: ['grandTotal'],
      },
    },
  },
});

export { buildOrderPdfConfig };
